import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Plus, Edit, Trash2, Star, Upload, X } from 'lucide-react';
import {
  AppButton, EmptyState, ConfirmModal, AppModal,
  AppCard, AppCardContent, StatusBadge,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import {
  listAdminTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  uploadCmsImage,
} from '../../utils/cmsAdminApi';
import './cms-admin.scss';

const emptyT = {
  name: '', role: '', company: '', quote: '',
  rating: 5, photoUrl: '', photoKey: '', order: 0, isActive: true,
};

const TestimonialsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {...} = edit
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAdminTestimonials();
      setItems(res.data || []);
    } catch {
      showToast('Failed to load testimonials', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => setEditing({ ...emptyT });
  const openEdit = (t) => setEditing({ ...t });
  const close = () => setEditing(null);

  const upd = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEditing(t => ({ ...t, [field]: v }));
  };

  const handleSave = async () => {
    if (!editing.name?.trim() || !editing.quote?.trim()) {
      return showToast('Name and quote are required', 'error');
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        role: editing.role || '',
        company: editing.company || '',
        quote: editing.quote,
        rating: parseInt(editing.rating, 10) || 5,
        photoUrl: editing.photoUrl || '',
        photoKey: editing.photoKey || '',
        order: parseInt(editing.order, 10) || 0,
        isActive: editing.isActive !== false,
      };
      if (editing._id) {
        await updateTestimonial(editing._id, payload);
        showToast('Testimonial updated');
      } else {
        await createTestimonial(payload);
        showToast('Testimonial added');
      }
      close();
      fetch();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteTestimonial(deleteId);
      showToast('Testimonial removed');
      setDeleteId(null);
      fetch();
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      return showToast('Please choose an image file', 'error');
    }
    setUploading(true);
    try {
      const res = await uploadCmsImage(file, 'testimonial-photo');
      setEditing(t => ({ ...t, photoKey: res.key, photoUrl: res.url }));
      showToast('Photo uploaded');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearPhoto = () => setEditing(t => ({ ...t, photoKey: '', photoUrl: '' }));

  return (
    <div className="cms-admin-page">
      {!loading && items.length > 0 && (
        <div className="cms-admin-toolbar">
          <AppButton variant="primary" className="ms-auto" onClick={openCreate}>
            <Plus size={16}/> Add Testimonial
          </AppButton>
        </div>
      )}

      {loading ? (
        <div className="cms-admin-loading">Loading testimonials…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<MessageSquare size={40} />}
          title="No testimonials yet"
          description="Add your first student testimonial to feature on your public site."
          action={
            <AppButton variant="primary" onClick={openCreate}>
              <Plus size={16}/> Add Testimonial
            </AppButton>
          }
        />
      ) : (
        <div className="cms-admin-grid">
          {items.map((t) => (
            <AppCard key={t._id} className="cms-admin-card">
              <AppCardContent>
                <div className="cms-admin-card__header">
                  <div className="cms-testimonial-person">
                    {t.photoUrl ? (
                      <img src={t.photoUrl} alt={t.name} className="cms-testimonial-photo" />
                    ) : (
                      <div className="cms-testimonial-photo cms-testimonial-photo--placeholder">
                        {t.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="cms-admin-card__title">{t.name}</h3>
                      {(t.role || t.company) && (
                        <div className="cms-testimonial-role">
                          {[t.role, t.company].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={t.isActive ? 'success' : 'default'}>
                    {t.isActive ? 'Active' : 'Hidden'}
                  </StatusBadge>
                </div>
                <div className="cms-testimonial-rating">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={i < (t.rating || 0) ? 'cms-star cms-star--on' : 'cms-star'}
                    />
                  ))}
                </div>
                <p className="cms-admin-card__excerpt">"{t.quote}"</p>
                <div className="cms-admin-card__actions">
                  <AppButton size="sm" variant="secondary" onClick={() => openEdit(t)}>
                    <Edit size={14}/> Edit
                  </AppButton>
                  <AppButton size="sm" variant="danger" onClick={() => setDeleteId(t._id)}>
                    <Trash2 size={14}/>
                  </AppButton>
                </div>
              </AppCardContent>
            </AppCard>
          ))}
        </div>
      )}

      <AppModal
        show={!!editing}
        onHide={close}
        title={editing?._id ? 'Edit Testimonial' : 'Add Testimonial'}
        size="md"
        footer={
          <>
            <AppButton variant="secondary" onClick={close} disabled={saving}>Cancel</AppButton>
            <AppButton variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </AppButton>
          </>
        }
      >
        {editing && (
          <div className="cms-editor-modal">
            <div className="cms-editor-field">
              <label htmlFor="t-name">Name</label>
              <input id="t-name" type="text" value={editing.name} onChange={upd('name')} />
            </div>
            <div className="cms-editor-field-row">
              <div className="cms-editor-field">
                <label htmlFor="t-role">Role</label>
                <input id="t-role" type="text" value={editing.role} onChange={upd('role')} placeholder="MERN Stack Graduate" />
              </div>
              <div className="cms-editor-field">
                <label htmlFor="t-company">Company / Outcome</label>
                <input id="t-company" type="text" value={editing.company} onChange={upd('company')} placeholder="Placed at TCS" />
              </div>
            </div>
            <div className="cms-editor-field">
              <label htmlFor="t-quote">Quote</label>
              <textarea id="t-quote" rows={4} value={editing.quote} onChange={upd('quote')} />
            </div>
            <div className="cms-editor-field-row">
              <div className="cms-editor-field">
                <label htmlFor="t-rating">Rating (1–5)</label>
                <input id="t-rating" type="number" min={1} max={5} value={editing.rating} onChange={upd('rating')} />
              </div>
              <div className="cms-editor-field">
                <label htmlFor="t-order">Display Order</label>
                <input id="t-order" type="number" value={editing.order} onChange={upd('order')} />
              </div>
            </div>
            <div className="cms-editor-field">
              <label htmlFor="t-photo">Photo</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handlePhotoUpload}
                className="cms-hidden-input"
              />
              <div className="cms-editor-actions">
                <AppButton
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload size={14}/> {uploading ? 'Uploading…' : 'Upload Photo'}
                </AppButton>
                {editing.photoUrl && (
                  <AppButton variant="danger" size="sm" onClick={clearPhoto}>
                    <X size={14}/> Remove
                  </AppButton>
                )}
              </div>
              <input
                id="t-photo"
                type="url"
                value={editing.photoKey ? '' : editing.photoUrl}
                onChange={(e) => setEditing(t => ({ ...t, photoUrl: e.target.value, photoKey: '' }))}
                placeholder="…or paste a URL"
                disabled={!!editing.photoKey}
              />
              {editing.photoUrl && (
                <img src={editing.photoUrl} alt="Preview" className="cms-editor-cover-preview" />
              )}
            </div>
            <label className="cms-editor-checkbox">
              <input type="checkbox" checked={editing.isActive !== false} onChange={upd('isActive')} />
              Show on public website
            </label>
          </div>
        )}
      </AppModal>

      <ConfirmModal
        show={!!deleteId}
        title="Delete testimonial?"
        message="This will permanently remove the testimonial."
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default TestimonialsList;
